import { useState } from "react";
import { createFileRoute, Link } from "@tanstack/react-router";
import { useQuery } from "@tanstack/react-query";
import { useServerFn } from "@tanstack/react-start";
import { RouteErrorBoundary, RouteNotFoundBoundary } from "@/components/route-boundaries";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import {
  Table, TableBody, TableCell, TableHead, TableHeader, TableRow,
} from "@/components/ui/table";
import { formatGBP } from "@/lib/format";
import {
  listBuyerOrderCentre,
  royalMailTrackingUrl,
  type OrderCentreTab,
} from "@/lib/order-centre.functions";
import { ExternalLink, Download, Mail, Package } from "lucide-react";
import { buildAuctionSlug } from "@/lib/auction-slug";
import { useTranslation } from "@/i18n";

export const Route = createFileRoute("/_authenticated/order-centre")({
  head: () => ({
    meta: [
      { title: "Order Centre — BidNova Auctions" },
      { name: "description", content: "Track all your orders in one place." },
      { name: "robots", content: "noindex" },
    ],
  }),
  component: OrderCentrePage,
  errorComponent: RouteErrorBoundary,
  notFoundComponent: RouteNotFoundBoundary,
});

const TAB_KEYS: { value: OrderCentreTab; key: string; fallback: string }[] = [
  { value: "active", key: "orderCentre.tabs.active", fallback: "Active" },
  { value: "waiting_dispatch", key: "orderCentre.tabs.waiting_dispatch", fallback: "Waiting for Dispatch" },
  { value: "shipped", key: "orderCentre.tabs.shipped", fallback: "Shipped" },
  { value: "delivered", key: "orderCentre.tabs.delivered", fallback: "Delivered" },
  { value: "cancelled", key: "orderCentre.tabs.cancelled", fallback: "Cancelled" },
  { value: "returned", key: "orderCentre.tabs.returned", fallback: "Returned" },
];

function OrderCentrePage() {
  const { t } = useTranslation("commerce");
  const [tab, setTab] = useState<OrderCentreTab>("active");
  const TABS = TAB_KEYS.map((tk) => ({ value: tk.value, label: t(tk.key, tk.fallback) }));
  const list = useServerFn(listBuyerOrderCentre);
  const { data, isLoading } = useQuery({
    queryKey: ["buyer-order-centre", tab],
    queryFn: () => list({ data: { tab } }),
  });

  const rows = data?.rows ?? [];

  return (
    <div className="mx-auto max-w-7xl px-4 sm:px-6 py-10 sm:py-12">
      <header className="mb-8">
        <p className="text-xs uppercase tracking-[0.2em] text-muted-foreground/70">{t("orderCentre.eyebrow", "My Purchases")}</p>
        <h1 className="mt-2 font-display text-4xl sm:text-5xl"><span className="text-gold-gradient">{t("orderCentre.title", "Order Centre")}</span></h1>
        <p className="mt-2 text-sm text-muted-foreground max-w-xl">
          {t("orderCentre.subtitle", "Track every order across its lifecycle. Read-only view — use Contact Seller for questions.")}
        </p>
      </header>

      <Tabs value={tab} onValueChange={(v) => setTab(v as OrderCentreTab)}>
        <TabsList className="mb-6 flex flex-wrap h-auto">
          {TABS.map((tabItem) => (
            <TabsTrigger key={tabItem.value} value={tabItem.value}>{tabItem.label}</TabsTrigger>
          ))}
        </TabsList>

        {TABS.map((tabItem) => (
          <TabsContent key={tabItem.value} value={tabItem.value}>
            {isLoading ? (
              <p className="text-muted-foreground py-8 text-center">{t("orderCentre.loading", "Loading…")}</p>
            ) : rows.length === 0 ? (
              <div className="rounded-lg border p-12 text-center">
                <Package className="mx-auto h-8 w-8 text-muted-foreground mb-2" />
                <p className="text-muted-foreground">{t("orderCentre.empty", "No orders in this category.")}</p>
              </div>
            ) : (
              <div className="rounded-lg border overflow-hidden">
                <Table>
                  <TableHeader>
                    <TableRow>
                      <TableHead>{t("orderCentre.table.orderNumber", "Order #")}</TableHead>
                      <TableHead>{t("orderCentre.table.item", "Item")}</TableHead>
                      <TableHead>{t("orderCentre.table.seller", "Seller")}</TableHead>
                      <TableHead>{t("orderCentre.table.amount", "Amount")}</TableHead>
                      <TableHead>{t("orderCentre.table.tracking", "Tracking")}</TableHead>
                      <TableHead>{t("orderCentre.table.dispatched", "Dispatched")}</TableHead>
                      <TableHead>{t("orderCentre.table.delivered", "Delivered")}</TableHead>
                      <TableHead>{t("orderCentre.table.rmStatus", "RM Status")}</TableHead>
                      <TableHead className="text-right">{t("orderCentre.table.actions", "Actions")}</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {rows.map((r) => (
                      <TableRow key={r.orderId}>
                        <TableCell className="font-mono text-xs">#{r.reference}</TableCell>
                        <TableCell className="max-w-[220px] truncate">
                          {r.auctionId ? (
                            <Link
                              to="/auction/$slug"
                              params={{
                                slug: r.auctionTitle
                                  ? buildAuctionSlug({ id: r.auctionId, title: r.auctionTitle })
                                  : r.auctionId,
                              }}
                              className="hover:underline"
                            >
                              {r.auctionTitle ?? t("orderCentre.auctionItem", "Auction item")}
                            </Link>
                          ) : (r.auctionTitle ?? "—")}
                        </TableCell>
                        <TableCell>{r.sellerName ?? "—"}</TableCell>
                        <TableCell>{formatGBP((r.amountPence ?? 0) / 100)}</TableCell>
                        <TableCell className="font-mono text-xs">{r.trackingNumber ?? "—"}</TableCell>
                        <TableCell>{r.dispatchedAt ? new Date(r.dispatchedAt).toLocaleDateString() : "—"}</TableCell>
                        <TableCell>{r.deliveredAt ? new Date(r.deliveredAt).toLocaleDateString() : "—"}</TableCell>
                        <TableCell>
                          {r.royalMailStatus ? <Badge variant="secondary">{r.royalMailStatus}</Badge> : "—"}
                        </TableCell>
                        <TableCell className="text-right space-x-1">
                          {r.trackingNumber && (
                            <Button size="sm" variant="outline" asChild>
                              <a href={royalMailTrackingUrl(r.trackingNumber)} target="_blank" rel="noreferrer">
                                <ExternalLink className="h-3 w-3 mr-1" />{t("orderCentre.track", "Track")}
                              </a>
                            </Button>
                          )}
                          {r.invoiceUrl && (
                            <Button size="sm" variant="outline" asChild>
                              <a href={r.invoiceUrl} target="_blank" rel="noreferrer">
                                <Download className="h-3 w-3 mr-1" />{t("orderCentre.invoice", "Invoice")}
                              </a>
                            </Button>
                          )}
                          {r.sellerId && (
                            <Button size="sm" variant="outline" asChild>
                              <Link to="/disputes" search={{ order: r.orderId } as never}>
                                <Mail className="h-3 w-3 mr-1" />{t("orderCentre.contact", "Contact")}
                              </Link>
                            </Button>
                          )}
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </div>
            )}
          </TabsContent>
        ))}
      </Tabs>
    </div>
  );
}
